import { ChangeDetectionStrategy, Component, computed, inject, signal, type Signal, type WritableSignal } from "@angular/core";
import { RouterOutlet, ChildrenOutletContexts, ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { slideInAnimation } from "@shared/animations/my-route-animation";
import { DetectLanguageChangeComponent } from "@shared/components/utility/detect-language-change/detect-language-change.component";
import { HeaderPageComponent } from "@shared/components/utility/header/pages/header-page.component";
import { NavbarComponent } from "@shared/components/utility/navbar/navbar.component";
import { TranslateService } from "@ngx-translate/core";
import { filter, map } from "rxjs/operators";
import { RouteHeaderData } from "@core/models/route-header-data.model";
import { LoadingStateHandlerService } from "@shared/services/loading-state/loading-state-handler.service";
import { COURSE_TERMS_GUARD_LOADING_CONTEXT } from "@shared/services/management-course-terms-conditions/management-course-terms-conditions.service";
import { SESSION_BOOTSTRAP_LOADING_CONTEXT } from "@shared/services/user-data/session-user-data.service";

@Component({
  selector: "app-header-layout",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./header-layout.component.html",
  styleUrls: ["./header-layout.component.scss"],
  animations: [slideInAnimation],
  imports: [RouterOutlet, DetectLanguageChangeComponent, HeaderPageComponent, NavbarComponent],
})
export class HeaderLayoutComponent {
  private readonly contexts: ChildrenOutletContexts = inject(ChildrenOutletContexts);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly loadingState: LoadingStateHandlerService = inject(LoadingStateHandlerService);

  readonly isCourseTermsGuardLoading: Signal<boolean> = toSignal(
    this.loadingState.getLoadingState(COURSE_TERMS_GUARD_LOADING_CONTEXT),
    { initialValue: false },
  );

  readonly isSessionBootstrapLoading: Signal<boolean> = toSignal(
    this.loadingState.getLoadingState(SESSION_BOOTSTRAP_LOADING_CONTEXT),
    { initialValue: false },
  );

  readonly isGlobalOverlayLoading: Signal<boolean> = computed(
    () => this.isCourseTermsGuardLoading() || this.isSessionBootstrapLoading(),
  );

  /** Misma razon que en `default-layout`: las reescribe `NavigationEnd`. */
  readonly currentPage: WritableSignal<string> = signal<string>("");
  readonly routeBackPage: WritableSignal<string> = signal<string>("");
  readonly bell: WritableSignal<boolean> = signal<boolean>(false);
  readonly showMobileNavbar: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((): RouteHeaderData => this.getChildRouteData())
    ).subscribe((data: RouteHeaderData) => this.updateHeaderFromRouteData(data));

    this.updateHeaderFromRouteData(this.getChildRouteData());
  }

  private getChildRouteData(): RouteHeaderData {
    let child: ActivatedRoute = this.route;
    while (child.firstChild) {
      child = child.firstChild;
    }

    return child.pathFromRoot.reduce(
      (merged: RouteHeaderData, route: ActivatedRoute) => ({
        ...merged,
        ...(route.snapshot.data as RouteHeaderData),
      }),
      {},
    );
  }

  private updateHeaderFromRouteData(data: RouteHeaderData): void {
    this.currentPage.set(data.currentPage ? this.translate.instant(data.currentPage) : "");
    this.routeBackPage.set(data.routeBackPage ?? "");
    this.bell.set(data.bell ?? false);
    this.showMobileNavbar.set(data.showMobileNavbar ?? false);
  }

  getAnimationData(): string | undefined {
    return this.contexts.getContext("primary")?.route?.snapshot?.data?.[
      "animation"
    ];
  }
}
